"use client";

import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TaskForm } from "@/components/TaskForm";
import { KanbanBoard } from "@/components/KanbanBoard";
import { TaskCommentsList } from "@/components/TaskCommentsList";
import { TaskCommentForm } from "@/components/TaskCommentForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Plus, Calendar, Filter, Search, LayoutList, LayoutGrid, MessageCircle, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { showSuccess, showError } from "@/utils/toast";

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'todo' | 'in_progress' | 'done';
  due_date: string | null;
  project_id: string;
  assignee_id: string | null;
  created_at: string;
  projects: { name: string };
  profiles: { first_name: string; last_name: string } | null;
  comment_count?: number;
}

const TasksPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'timeline'>('list');
  const [filterStatus, setFilterStatus] = useState<'all' | 'todo' | 'in_progress' | 'done'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);

  const { data: tasks, isLoading, refetch } = useQuery<Task[]>({
    queryKey: ["tasks", projectId, profile?.company_id, filterStatus, searchTerm],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      let query = supabase
        .from("tasks")
        .select("*, projects(name), profiles(first_name, last_name)")
        .eq("company_id", profile.company_id)
        .order("created_at", { ascending: false });

      if (projectId) query = query.eq("project_id", projectId);
      if (filterStatus !== 'all') query = query.eq("status", filterStatus);
      if (searchTerm) query = query.ilike("title", `%${searchTerm}%`);

      const { data, error } = await query;
      if (error) {
        if (error.message.includes('permission')) {
          showError('Access denied. Contact your admin.');
          return [];
        }
        throw error;
      }
      return data || [];
    },
    enabled: !!profile?.company_id,
    retry: (failureCount, error: any) => {
      if (error.message.includes('permission')) return false;
      return failureCount < 3;
    },
  });

  const { data: commentCounts } = useQuery({
    queryKey: ["task_comment_counts", tasks?.map((t) => t.id), profile?.company_id],
    queryFn: async () => {
      if (!tasks?.length || !profile?.company_id) return {};
      const taskIds = tasks.map((t) => t.id);

      // Accurate counts via per-task HEAD count requests
      const results = await Promise.all(
        taskIds.map(async (id) => {
          const { count, error } = await supabase
            .from("task_comments")
            .select("*", { count: "exact", head: true })
            .eq("task_id", id)
            .eq("company_id", profile.company_id);
          if (error) return [id, 0] as const;
          return [id, count || 0] as const;
        })
      );

      return results.reduce((acc, [id, c]) => {
        (acc as any)[id] = c;
        return acc;
      }, {} as Record<string, number>);
    },
    enabled: !!tasks?.length && !!profile?.company_id,
  });

  const tasksWithCounts = tasks?.map(task => ({
    ...task,
    comment_count: (commentCounts as any)?.[task.id] || 0,
  })) || [];

  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const { data } = await supabase.from("projects").select("*").eq("id", projectId).single();
      return data;
    },
    enabled: !!projectId && !!profile?.company_id,
    retry: (failureCount, error: any) => {
      if (error.message.includes('permission')) return false;
      return failureCount < 3;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase.from("tasks").delete().eq("id", taskId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      showSuccess("Task deleted!");
    },
    onError: (error: any) => {
      if (error.message.includes('permission')) {
        showError('Access denied. Contact your admin.');
      } else {
        showError("Failed to delete task");
      }
    },
  });

  const generateRecurringMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.company_id) throw new Error("No company");
      const { data, error } = await supabase.functions.invoke('generate-recurring-tasks', {
        body: { company_id: profile.company_id },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      showSuccess(`Generated ${data.generated} recurring task instances!`);
    },
    onError: (error: any) => {
      if (error.message.includes('permission')) {
        showError('Access denied. Contact your admin.');
      } else {
        showError("Failed to generate recurring tasks");
      }
    },
  });

  const handleTaskCreated = () => {
    setShowForm(false);
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };

  const timelineTasks = (tasksWithCounts || [])
    .filter(task => task.due_date)
    .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime());

  if (isLoading) return <div className="p-4">Loading tasks...</div>;

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">
            {project ? `Tasks for ${project.name}` : 'All Tasks'}
          </h1>
          <p className="text-muted-foreground">{(tasksWithCounts || []).length} tasks</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="mr-2 h-4 w-4" />
            {showForm ? "Cancel" : "New Task"}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => generateRecurringMutation.mutate()} 
            className="ml-2"
            disabled={generateRecurringMutation.isPending}
          >
            <Clock className="mr-2 h-4 w-4" />
            Generate Recurring
          </Button>
          <ToggleGroup type="single" value={viewMode} onValueChange={(value: string) => setViewMode(value as 'list' | 'kanban' | 'timeline')}>
            <ToggleGroupItem value="list" aria-label="List view">
              <LayoutList className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="kanban" aria-label="Kanban view">
              <LayoutGrid className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="timeline" aria-label="Timeline view">
              <Calendar className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          <Select value={filterStatus} onValueChange={(value: 'all' | 'todo' | 'in_progress' | 'done') => setFilterStatus(value)}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Filter Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="todo">To Do</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
        </div>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Create New Task {projectId && `(for ${project?.name})`}</CardTitle>
          </CardHeader>
          <CardContent>
            <TaskForm projectId={projectId} onSuccess={handleTaskCreated} />
          </CardContent>
        </Card>
      )}

      <Tabs value={viewMode} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="list">List View</TabsTrigger>
          <TabsTrigger value="kanban">Kanban Board</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="space-y-4">
          <div className="space-y-6">
            {tasksWithCounts.map((task: Task) => (
              <Collapsible key={task.id}>
                <CollapsibleTrigger asChild>
                  <Card className="hover:shadow-md transition-shadow cursor-pointer">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{task.title}</CardTitle>
                          <Badge variant={task.status === 'done' ? "default" : "secondary"}>{task.status}</Badge>
                          {task.due_date && (
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground mt-2">
                              <Calendar className="h-4 w-4" />
                              <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                          {task.project_id && <p className="text-xs text-muted-foreground">Project: {task.projects?.name}</p>}
                          {task.assignee_id && <p className="text-xs text-muted-foreground">Assignee: {task.profiles?.first_name} {task.profiles?.last_name}</p>}
                          {task.comment_count! > 0 && (
                            <div className="flex items-center space-x-2 text-xs text-muted-foreground mt-2">
                              <MessageCircle className="h-3 w-3" />
                              <span>{task.comment_count} comment{task.comment_count! > 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <Button variant="outline" size="sm">Edit</Button>
                          <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate(task.id)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 pb-4">
                      <div className={`w-4 h-4 border-l-4 border-r-4 border-t-4 border-b-0 border-gray-400 rotate-0 transition-transform duration-200 ml-auto mr-4`} style={{ borderLeftColor: 'transparent', borderRightColor: 'transparent', borderTopColor: 'currentColor' }}></div>
                    </CardContent>
                  </Card>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 mt-2">
                  <TaskCommentsList taskId={task.id} />
                  <div className="px-2">
                    <TaskCommentForm
                      taskId={task.id}
                      onCommentAdded={() => {
                        // Refresh both the comments for this task and the counts
                        queryClient.invalidateQueries({ queryKey: ["task_comments", task.id] });
                        queryClient.invalidateQueries({ queryKey: ["task_comment_counts"] });
                      }}
                      placeholder="Write a comment..."
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
          {(tasksWithCounts || []).length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <p className="text-muted-foreground">No tasks match your filters. Create one above!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="kanban" className="mt-4">
          <KanbanBoard projectId={projectId} />
        </TabsContent>
        <TabsContent value="timeline" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Task Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative border-l-4 border-primary/20 pl-6 space-y-8">
                {timelineTasks.map((task) => (
                  <div key={task.id} className="relative">
                    <div className="absolute left-0 top-0 w-4 h-full bg-gradient-to-b from-primary/20 to-transparent"></div>
                    <div className={`absolute left-[-16px] w-4 h-4 rounded-full ${task.status === 'done' ? 'bg-green-500' : task.due_date && new Date(task.due_date) < new Date() ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                    <div className="ml-4 p-4 bg-card rounded-lg shadow-sm border">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-medium">{task.title}</span>
                        <Badge variant={task.status === 'done' ? "default" : "secondary"}>{task.status}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{task.description}</p>
                      <div className="text-xs text-muted-foreground">
                        Due: {new Date(task.due_date!).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {timelineTasks.length === 0 && <p className="text-muted-foreground">No tasks with due dates.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TasksPage;